import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { Logo } from "./Logo";
import { QuoteLink } from "./QuoteLink";
import { useLang, UI } from "../i18n";

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, prefix, altPath } = useLang();
  const s = UI[lang];

  useEffect(() => setOpen(false), [location.pathname]);

  const NAV = [
    { to: `${prefix}/routes`, label: s.nav_routes, testid: "nav-routes-link" },
    { to: `${prefix}/blog`, label: s.nav_blog, testid: "nav-travel-journal-link" },
    { to: `${prefix}/resources`, label: s.nav_resources, testid: "nav-resources-link" },
    { to: `${prefix}/#faq`, label: s.nav_faq, testid: "nav-faq-link" },
    { to: `${prefix}/#contact`, label: s.nav_contact, testid: "nav-contact-link" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-[#0B0F0C]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
          <Link to={prefix || "/"} data-testid="nav-home-link" aria-label="Pet Taxi London home">
            <Logo dark />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.testid}
                href={n.to}
                data-testid={n.testid}
                className="text-sm font-medium text-champagne/70 hover:text-white transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to={altPath}
              data-testid="lang-switch"
              className="flex items-center gap-1.5 font-mono text-xs font-semibold text-champagne/70 hover:text-white border border-champagne/25 rounded-full px-3 py-1.5 transition-colors"
              aria-label={lang === "en" ? "Passer en français" : "Switch to English"}
            >
              <Languages className="w-3.5 h-3.5" /> {lang === "en" ? "FR" : "EN"}
            </Link>
            <QuoteLink
              testid="nav-booking-cta"
              className="rounded-full bg-[#C3D9B6] text-[#14210F] text-sm font-bold px-6 py-2.5 hover:bg-[#B2CCA3] transition-colors"
            >
              {s.nav_quote}
            </QuoteLink>
          </div>
          <button
            className="lg:hidden p-2 text-champagne"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-[#0B0F0C] border-t border-white/10 px-6 py-6 space-y-4" data-testid="nav-mobile-menu">
            {NAV.map((n) => (
              <a key={n.testid} href={n.to} className="block text-base font-medium text-champagne">
                {n.label}
              </a>
            ))}
            <Link to={altPath} className="flex items-center gap-2 text-base font-medium text-[#A9C29A]">
              <Languages className="w-4 h-4" /> {lang === "en" ? "Version française" : "English version"}
            </Link>
            <QuoteLink testid="nav-booking-cta-mobile" className="block rounded-full bg-[#C3D9B6] text-[#14210F] text-center font-bold px-6 py-3">
              {s.nav_quote}
            </QuoteLink>
          </div>
        )}
      </div>
    </header>
  );
}
